import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { useQuery } from "@apollo/client";

import { useFeatureGates } from "../../../cof-feature-gates/hooks";
import { type AbsoluteRoute, type RouteParams } from "../../../core/routes";
import { CofConfigId } from "../../../gql-generated/types";
import { type ApolloResult } from "../../../shared/models";
import {
    type GetAllPreSubmissionInfoWithOrgsForLensSpaceCreationV2Query,
    type GetAllPreSubmissionInfoWithOrgsForLensSpaceCreationV2QueryVariables,
    type GetAllPresubmissionInfoWithOrgResourcesAndOrgsAvailableForLensSpaceCreationQuery,
    type GetAllPresubmissionInfoWithOrgResourcesAndOrgsAvailableForLensSpaceCreationQueryVariables,
    getAllPreSubmissionInfoWithOrgsForLensSpaceCreationV2,
    getAllPresubmissionInfoWithOrgResourcesAndOrgsAvailableForLensSpaceCreation,
} from "../../api/queries";
import { type LensSubmissionFlowState } from "../../models";
import { useSubmitLensConfiguration } from "../useSubmitLensConfiguration";

import { getFlowSubmissionState } from "./getFlowSubmissionState";
import { getLensSubmissionRouteV2 } from "./getLensSubmissionRouteV2";
import { getLensSubmissionRouteV3 } from "./getLensSubmissionRouteV3";

export const useLensSubmissionFlow = (): ApolloResult<{
    route: string;
    state: LensSubmissionFlowState | null;
}> => {
    const { submissionId = "" } = useParams<RouteParams[AbsoluteRoute.SUBMIT_FLOW_REDIRECTOR]>();

    const { configs } = useFeatureGates();
    const isMyLensesOrgsV2Enabled = configs[CofConfigId.MyLensesOrgsV2].enabled;

    const {
        submitLensState: { submitUnderDifferentUser },
    } = useSubmitLensConfiguration();

    const {
        data,
        loading: v1Loading,
        error: v1Error,
    } = useQuery<
        GetAllPresubmissionInfoWithOrgResourcesAndOrgsAvailableForLensSpaceCreationQuery,
        GetAllPresubmissionInfoWithOrgResourcesAndOrgsAvailableForLensSpaceCreationQueryVariables
    >(getAllPresubmissionInfoWithOrgResourcesAndOrgsAvailableForLensSpaceCreation, {
        variables: { submissionId },
        skip: isMyLensesOrgsV2Enabled,
    });

    const {
        data: dataV2,
        loading: v2Loading,
        error: v2Error,
    } = useQuery<
        GetAllPreSubmissionInfoWithOrgsForLensSpaceCreationV2Query,
        GetAllPreSubmissionInfoWithOrgsForLensSpaceCreationV2QueryVariables
    >(getAllPreSubmissionInfoWithOrgsForLensSpaceCreationV2, {
        variables: { submissionId, limit: 100, cursor: "" },
        skip: !isMyLensesOrgsV2Enabled,
    });

    const loading = isMyLensesOrgsV2Enabled ? v2Loading : v1Loading;
    const error = isMyLensesOrgsV2Enabled ? v2Error : v1Error;

    const route = useMemo(() => {
        if (isMyLensesOrgsV2Enabled && dataV2) {
            return getLensSubmissionRouteV2(
                dataV2.getPreSubmissionInfo,
                dataV2.myLensesOrganizationsForLensSpaceCreation.organizationList,
                submissionId,
                submitUnderDifferentUser,
            );
        }

        if (data) {
            const {
                getPreSubmissionInfo,
                getMyLensesOrganizations,
                getMyLensesOrganizationsAvailableForLensSpaceCreation,
            } = data;

            return getLensSubmissionRouteV3(
                getPreSubmissionInfo,
                getMyLensesOrganizations,
                getMyLensesOrganizationsAvailableForLensSpaceCreation,
                submissionId,
                submitUnderDifferentUser,
            );
        }

        return "";
    }, [submissionId, submitUnderDifferentUser, data, isMyLensesOrgsV2Enabled, dataV2]);

    const state = useMemo(() => {
        return route ? getFlowSubmissionState(route) : null;
    }, [route]);

    return { loading, error, route, state };
};
